import React, { useState } from 'react';
import { 
  Building2, Tent, HardHat, Square, Layers, Trees,
  Phone, Mail, MapPin, Award, Zap, CheckCircle, FileText,
  Star, Menu, X, ArrowRight, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from 'sonner';
import { services, testimonials, whyChooseUs, galleryImages, companyInfo } from '../utils/mock';
import Logo from './Logo';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Building2, Tent, HardHat, Square, Layers, Trees, Award, Zap, CheckCircle, FileText
};

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation manuelle du champ service
    if (!formData.service || formData.service.trim() === '') {
      toast.error('Veuillez sélectionner un service');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Envoyer la demande à l'API
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message
      });
      
      if (response.status === 200) {
        toast.success('Demande envoyée avec succès ! Nous vous contacterons rapidement.');
        // Réinitialiser le formulaire
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer ou nous appeler directement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Logo className="h-14" />

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('accueil')} className="text-gray-700 hover:text-cyan-600 font-medium transition-colors">
                Accueil
              </button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-cyan-600 font-medium transition-colors">
                Services
              </button>
              <button onClick={() => scrollToSection('galerie')} className="text-gray-700 hover:text-cyan-600 font-medium transition-colors">
                Galerie
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-cyan-600 font-medium transition-colors">
                Contact
              </button>
              <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}>
                <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
                  <Phone className="w-4 h-4 mr-2" />
                  {companyInfo.phone}
                </Button>
              </a>
            </nav>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 space-y-3 border-t border-gray-100">
              <button onClick={() => scrollToSection('accueil')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">
                Accueil
              </button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">
                Services
              </button>
              <button onClick={() => scrollToSection('galerie')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">
                Galerie
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg">
                Contact
              </button>
              <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="block px-4">
                <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
                  <Phone className="w-4 h-4 mr-2" />
                  Appelez-nous
                </Button>
              </a>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="accueil" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-cyan-100 text-cyan-800 px-4 py-2 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                <span>Votre partenaire propreté</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Nettoyage professionnel pour vos{' '}
                <span className="text-cyan-600">locaux & espaces</span>
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                EKJ Nettoyage intervient pour l'entretien de vos bureaux, campings, chantiers, vitres et espaces verts. Professionnalisme et qualité garantis.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-cyan-600 hover:bg-cyan-700 text-white text-lg px-8 py-6"
                  onClick={() => scrollToSection('contact')}
                >
                  Demander un devis gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50 text-lg px-8 py-6"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200">
                <div>
                  <div className="text-3xl font-bold text-cyan-600">10+</div>
                  <div className="text-sm text-gray-600">Ans d'expérience</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-cyan-600">500+</div>
                  <div className="text-sm text-gray-600">Clients satisfaits</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-cyan-600">100%</div>
                  <div className="text-sm text-gray-600">Qualité garantie</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_547aee01-8839-48d6-9fa7-736bae88c1ba/artifacts/kur184q2_1000106828.jpg"
                  alt="Nettoyage professionnel" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/20 to-transparent"></div>
              </div>
              
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-start space-x-4">
                  <div className="bg-cyan-100 p-3 rounded-lg">
                    <Award className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Certification Pro</div>
                    <div className="text-sm text-gray-600">Services agréés et assurés</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nos Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Une gamme complète de prestations adaptées à tous vos besoins en nettoyage professionnel
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-2 hover:border-cyan-600 hover:shadow-xl transition-all duration-300 group">
                  <CardHeader>
                    <div className="w-14 h-14 bg-cyan-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-cyan-600 transition-colors">
                      <Icon className="w-7 h-7 text-cyan-600 group-hover:text-white transition-colors" />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-cyan-600 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Pourquoi choisir EKJ Nettoyage ?</h2>
            <p className="text-xl text-cyan-100 max-w-2xl mx-auto">
              Nous mettons notre expertise et notre savoir-faire à votre service
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <div key={item.id} className="text-center">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-cyan-100">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="galerie" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nos Réalisations</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Découvrez la qualité de nos interventions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {galleryImages.map((item) => (
              <div key={item.id} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-end p-6">
                  <div className="text-white">
                    <div className="text-sm font-medium text-cyan-400 mb-1">{item.category}</div>
                    <div className="text-lg font-bold">{item.title}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Témoignages Clients</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ce que nos clients disent de nous
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2">
                <CardHeader>
                  <div className="flex items-center space-x-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <CardDescription className="text-base text-gray-700 italic">
                    "{testimonial.text}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-cyan-600">{testimonial.company}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Contactez-nous</h2>
              <p className="text-xl text-gray-600 mb-8">
                Besoin d'un devis ou d'une intervention ? Nous sommes à votre écoute.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-cyan-100 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Téléphone</div>
                    <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="text-cyan-600 hover:text-cyan-700 text-lg">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-cyan-100 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Email</div>
                    <a href={`mailto:${companyInfo.email}`} className="text-cyan-600 hover:text-cyan-700">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-cyan-100 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Adresse</div>
                    <div className="text-gray-600">
                      {companyInfo.address}<br />
                      {companyInfo.postalCode} {companyInfo.city}
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-cyan-100 p-3 rounded-lg">
                    <FileText className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">SIRET</div>
                    <div className="text-gray-600">{companyInfo.siret}</div>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-cyan-50 rounded-xl border-2 border-cyan-100">
                <h3 className="font-bold text-gray-900 mb-2">Horaires d'intervention</h3>
                <p className="text-gray-600">Lundi - Vendredi : 7h00 - 19h00</p>
                <p className="text-gray-600">Samedi : Sur rendez-vous</p>
                <p className="text-sm text-cyan-600 mt-2">Interventions d'urgence possibles</p>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="border-2 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl">Demande de devis gratuit</CardTitle>
                <CardDescription>Remplissez le formulaire, nous vous répondons rapidement</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Votre nom"
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="votre@email.fr"
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="06 12 34 56 78"
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service">Service souhaité *</Label>
                    <Select 
                      value={formData.service} 
                      onValueChange={handleServiceChange}
                    >
                      <SelectTrigger className="mt-2" id="service">
                        <SelectValue placeholder="Sélectionnez un service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.id} value={service.title}>
                            {service.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Décrivez votre besoin..."
                      rows={4}
                      className="mt-2"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-6 text-lg" disabled={isSubmitting}>
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                    {!isSubmitting && <ArrowRight className="w-5 h-5 ml-2" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <Logo className="h-12 mb-4" variant="white" />
              <p className="text-gray-400">
                {companyInfo.description}
              </p>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4">Services</h3>
              <ul className="space-y-2 text-gray-400">
                {services.slice(0, 4).map((service) => (
                  <li key={service.id}>{service.title}</li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>{companyInfo.phone}</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>{companyInfo.email}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{companyInfo.fullAddress}</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>SIRET: {companyInfo.siret}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} EKJ Nettoyage. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
